// soccer_admin/src/pages/SponsorshipRequests.jsx
import React, { useEffect, useState } from 'react';
import { 
  EnvelopeIcon, UserIcon, BuildingOfficeIcon, CalendarIcon,
  CheckCircleIcon, XCircleIcon, ClockIcon, EyeIcon,
  ArrowPathIcon, PencilIcon
} from '@heroicons/react/24/outline';
import { fetchSponsorshipRequests, updateSponsorshipStatus } from '../services/api';

const statusColors = {
  pending: 'bg-yellow-600/20 text-yellow-400',
  approved: 'bg-green-600/20 text-green-400',
  rejected: 'bg-red-600/20 text-red-400',
  contacted: 'bg-blue-600/20 text-blue-400'
};

const statusLabels = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  contacted: 'Contacted'
};

const tierBadges = {
  bronze: 'bg-amber-700 text-white',
  silver: 'bg-gray-400 text-gray-900',
  gold: 'bg-yellow-600 text-white',
  diamond: 'bg-purple-600 text-white'
};

const SponsorshipRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await fetchSponsorshipRequests();
      setRequests(res.data);
    } catch (err) {
      console.error(err);
      alert('Failed to load sponsorship requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    if (!window.confirm(`Change status to "${statusLabels[newStatus]}"?`)) return;
    setUpdatingId(id);
    try {
      await updateSponsorshipStatus(id, newStatus);
      await loadRequests();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        <span className="ml-3 text-white text-lg">Loading requests...</span>
      </div>
    );
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const totalCount = requests.length;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <EnvelopeIcon className="h-8 w-8 text-yellow-400" />
              Sponsorship Requests
            </h1>
            <p className="text-gray-400 mt-2">Manage sponsorship applications from companies and individuals</p>
          </div>
          <div className="flex gap-3">
            <span className="bg-yellow-500/20 text-yellow-300 px-3 py-1 rounded-full text-sm font-medium">
              Pending: {pendingCount}
            </span>
            <span className="bg-gray-600/30 text-gray-300 px-3 py-1 rounded-full text-sm font-medium">
              Total: {totalCount}
            </span>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="min-w-full text-white">
            <thead className="bg-gray-700/50 border-b border-gray-700">
              <tr className="text-left text-gray-400 text-sm">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Applicant</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req, idx) => (
                <tr key={req.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition">
                  <td className="px-4 py-3 text-gray-400">#{req.id}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-white">{req.name}</span>
                      <span className="text-xs text-gray-400">{req.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{req.company || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${tierBadges[req.tier] || 'bg-gray-600'}`}>
                      {req.tier}
                    </span>
                    <span className="text-xs text-gray-400 ml-1">${req.tier_price}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[req.status]}`}>
                      {statusLabels[req.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-sm">
                    {new Date(req.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => toggleExpand(req.id)}
                        className="text-blue-400 hover:text-blue-300 transition"
                        title="View Details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      {req.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(req.id, 'approved')}
                            disabled={updatingId === req.id}
                            className="text-green-400 hover:text-green-300 transition disabled:opacity-50"
                            title="Approve"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleStatusChange(req.id, 'rejected')}
                            disabled={updatingId === req.id}
                            className="text-red-400 hover:text-red-300 transition disabled:opacity-50"
                            title="Reject"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleStatusChange(req.id, 'contacted')}
                            disabled={updatingId === req.id}
                            className="text-blue-400 hover:text-blue-300 transition disabled:opacity-50"
                            title="Mark Contacted"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {updatingId === req.id && (
                        <ArrowPathIcon className="h-5 w-5 animate-spin text-yellow-400" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {requests.length === 0 && (
          <div className="text-center text-gray-400 py-12">
            No sponsorship requests yet.
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {requests.map((req) => (
          <div key={req.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-white font-bold">{req.name}</p>
                <p className="text-gray-400 text-sm">{req.email}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[req.status]}`}>
                {statusLabels[req.status]}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-gray-400">Company:</span> <span className="text-white">{req.company || '—'}</span></div>
              <div><span className="text-gray-400">Tier:</span> <span className="text-yellow-300 font-medium">{req.tier}</span></div>
              <div><span className="text-gray-400">Price:</span> <span className="text-white">{req.tier_price}</span></div>
              <div><span className="text-gray-400">Date:</span> <span className="text-white">{new Date(req.created_at).toLocaleDateString()}</span></div>
            </div>
            {req.message && (
              <div className="mt-2 bg-gray-700/30 rounded-lg p-2 text-sm text-gray-300 max-h-20 overflow-y-auto">
                {req.message}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {req.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleStatusChange(req.id, 'approved')}
                    disabled={updatingId === req.id}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-1.5 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <CheckCircleIcon className="h-4 w-4" /> Approve
                  </button>
                  <button
                    onClick={() => handleStatusChange(req.id, 'rejected')}
                    disabled={updatingId === req.id}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-1.5 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <XCircleIcon className="h-4 w-4" /> Reject
                  </button>
                  <button
                    onClick={() => handleStatusChange(req.id, 'contacted')}
                    disabled={updatingId === req.id}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-1.5 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" /> Contacted
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {requests.length === 0 && (
          <div className="text-center text-gray-400 py-12">No requests found.</div>
        )}
      </div>
    </div>
  );
};

export default SponsorshipRequests;